import React, { FC, useContext, useState, useEffect } from 'react';
import { debounce } from 'debounce';
// actions
import { selectTracks } from 'src/@operations/actions';
// selectors
import { useDataSelectors } from 'src/@operations/reducers/dataReducer';
// common components
import { StyledMap, MapControl, GeocoderPin } from '@ems/client-design-system';
// functions
import {
  useMapRef,
  useMapApis,
  useMapProps,
  useDatesDataForMap,
  useMapConfig,
} from 'src/@app/functions/map';
import { defineBbox } from 'src/@app/functions/bbox';
import { useCircleRanges } from 'src/@app/functions/rangeCircle';
import { useOperationsDataInMap, useFilterMap } from 'src/@operations/functions/map';
import { useConfigSelectors } from 'src/@app/reducers/configReducer';
import { useLanguageSelectors } from 'src/@app/reducers/languageReducer';
// context
import { OperationDispatchContext } from 'src/@operations/providers/OperationsStateProvider';
import {
  flyTo,
  useMapHoverOperations,
  useMapClick,
  useMapSelection,
  useMapHover,
  useMapMultiSelect,
  whenMapHasLoadedStyle,
} from 'src/utils/mapHelpers';
import { useElevation } from 'src/utils/geocoding';
import { OperationPopup, LocationPopup, MapSelectionArea } from 'src/components';
import { DEFAULT_BBOX_DISTANCE, FLY_TO_DURATION } from 'src/constants';
import { useFilterSelectors } from 'src/@operations/reducers';

export const MapContainer: FC = () => {
  const dispatcher: any = useContext(OperationDispatchContext);
  // map ref
  const [mapNode, mapRef] = useMapRef();
  // get map apis
  const mapApis: any = useMapApis(mapNode);
  // get map props from config
  const { viewportFromProps, mapboxApiAccessToken, ...mapProps } = useMapProps('2D');
  // viewport in state
  const [viewport, setViewport] = useState(viewportFromProps);
  // get mapbox config values required to add source and styles
  const mapBoxConfig = useMapConfig();
  // Configuration
  const configSelectors = useConfigSelectors();
  const {
    globals: { distanceUnits, altitudeUnits },
  } = configSelectors.getConfig();
  // Translation
  const languageSelectors = useLanguageSelectors();
  const {
    fields: { operations: opsFields },
    abbreviations: { operations: opsAbbreviation },
    components: {
      labels: {
        backToCenter: backToCenterLabel,
        search: searchLabel,
        addPin: addPinLabel,
        removePin: removePinLabel,
        lat: latLabel,
        lng: lngLabel,
        amsl: amslLabel,
      },
    },
  } = languageSelectors.getLanguage();
  const filterSelectors = useFilterSelectors();
  const selectedFilters = filterSelectors.getRegularFilters();
  const isFilterApplied = filterSelectors.getIfFilterApplied();
  const timeFilter = filterSelectors.getTimeValues();

  // get layers for styling
  const { layers, opstypeFilter } = useFilterMap(
    mapApis,
    mapBoxConfig,
    selectedFilters,
    timeFilter,
    isFilterApplied
  );

  // get values when dates change
  const { datesArray, dateRangeMapping } = useDatesDataForMap(mapApis, mapBoxConfig, layers);

  const dataSelectors = useDataSelectors();
  const requiredDataForMap = dataSelectors.getRequiredDataForMap();
  const { selectedOperations } = dataSelectors.getDataInformation();

  const [layerCount, setLayerCount] = useState<number>(0);
  if (mapApis) {
    whenMapHasLoadedStyle(mapApis).then(() => {
      const layers = mapApis.getStyle().layers;
      if (layers.length !== layerCount) {
        setLayerCount(layers.length);
      }
    });
  }

  useOperationsDataInMap(
    mapApis,
    dateRangeMapping,
    datesArray,
    mapBoxConfig,
    requiredDataForMap,
    selectedOperations,
    opstypeFilter,
    viewport,
    layerCount
  );

  const resetView = () => {
    if (mapApis) {
      const resetViewport = Object.assign({}, viewportFromProps, { zoom: viewport.zoom });
      flyTo(mapApis, resetViewport).then(() => {
        setViewport(Object.assign({}, viewport, resetViewport));
      });
    }
  };

  const labels = Object.assign(opsFields, opsAbbreviation);

  const { hoveredOperation, handleHover } = useMapHoverOperations(
    viewport,
    mapApis,
    datesArray,
    opstypeFilter
  );

  const [showSelected, setShowSelected] = useState(false);
  const [selectionBounds, setSelectionBounds]: any = useState(null);
  const [geocoding, updateGeocoding] = useState<{ longitude: number; latitude: number }>({
    longitude: 0,
    latitude: 0,
  });
  const [locationAddress, updateLocationAddress] = useState<any>([]);
  const [isSearchOpen, updateDialogStatus] = useState<boolean>(false);
  const [isLocationTagOpen, updateLocationTagOpen] = useState<boolean>(false);
  const [drag, updateDragStatus] = useState<boolean>(false);

  // geocoder bbox
  const [bbox, updateBbox] = useState<number[]>([]);
  useEffect(() => {
    // define the geocoder bbox at the start
    updateBbox(defineBbox(viewport, mapBoxConfig.geocoderBbox, DEFAULT_BBOX_DISTANCE));
  }, []);

  // Set max bounds for pan
  // react-map-gl doesn't support setMaxBound yet so we handle it manually
  const onViewportChange = viewport => {
    if (viewport.longitude < bbox[0]) {
      viewport.longitude = bbox[0];
    } else if (viewport.longitude > bbox[2]) {
      viewport.longitude = bbox[2];
    }
    if (viewport.latitude < bbox[1]) {
      viewport.latitude = bbox[1];
    } else if (viewport.latitude > bbox[3]) {
      viewport.latitude = bbox[3];
    }
    setViewport(viewport);
  };

  const { addRemoveCircles } = useCircleRanges(mapApis, distanceUnits);
  const { elevation, place } = useElevation(mapBoxConfig, mapboxApiAccessToken, geocoding);

  const setSelectedOperations = operations => {
    // TODO restrict the number of times the action needs to be dispatched
    selectTracks(operations, dispatcher);
  };

  const handleOnResult = ({ result }) => {
    const { center, text } = result;
    if (typeof center !== 'undefined' && center.length === 2) {
      setTimeout(() => {
        updateGeocoding({ longitude: center[0], latitude: center[1] });
        updateLocationAddress(text);
        updateLocationTagOpen(true);
      }, FLY_TO_DURATION);
    }
  };

  const goToSelectedAddress = viewportUpdated => {
    const { longitude, latitude, zoom } = viewportUpdated;
    if (mapApis !== null) {
      // @ts-ignore
      mapApis.flyTo({
        center: [longitude, latitude],
        zoom,
        duration: FLY_TO_DURATION,
      });
      setTimeout(() => {
        const { bearing, pitch, altitude } = viewport;
        setViewport({
          longitude,
          latitude,
          zoom,
          bearing,
          pitch,
          altitude,
        });
        addRemoveCircles({ longitude, latitude });
      }, FLY_TO_DURATION);
    }
  };

  const addPinToCentre = () => {
    // toggle the feature
    updateLocationAddress('');
    if (geocoding.longitude) {
      // remove the pin if it's already added to the map
      updateGeocoding({ longitude: 0, latitude: 0 });
      addRemoveCircles(null);
    } else {
      const { longitude, latitude } = viewport;
      updateGeocoding({ longitude, latitude });
      addRemoveCircles({ longitude, latitude });
      updateLocationTagOpen(true);
    }
  };

  const { handleClick, selectedOperation } = useMapClick(
    hoveredOperation,
    setShowSelected,
    mapApis,
    datesArray,
    opstypeFilter,
    selectedOperations,
    setSelectedOperations
  );

  useMapSelection(selectedOperations, mapApis);
  const { removeHovered } = useMapHover(hoveredOperation, mapApis, drag);

  const { dragRotate } = useMapMultiSelect(
    datesArray,
    mapNode,
    mapApis,
    setSelectedOperations,
    setSelectionBounds,
    setShowSelected,
    mapBoxConfig,
    opstypeFilter
  );

  const { latitude, longitude } = geocoding;

  return (
    <div className="map_wrapper">
      <div className="map">
        <StyledMap
          viewport={viewport}
          onViewportChange={viewport => {
            viewport.maxPitch = 0;
            onViewportChange(viewport);
          }}
          mapboxApiAccessToken={mapboxApiAccessToken}
          {...mapProps}
          dragRotate={dragRotate}
          onClick={isSearchOpen ? undefined : handleClick}
          onHover={isSearchOpen ? undefined : debounce(handleHover, 100)}
          ref={mapRef}>
          {!isSearchOpen &&
            !drag &&
            showSelected &&
            selectedOperation &&
            selectedOperation.latitude &&
            selectedOperation.longitude && (
              <OperationPopup
                selectedOperation={selectedOperation}
                onClose={() => setShowSelected(false)}
                labels={labels}
              />
            )}
          {!isSearchOpen &&
            !drag &&
            !showSelected &&
            hoveredOperation &&
            hoveredOperation.latitude &&
            hoveredOperation.longitude && (
              <OperationPopup
                selectedOperation={hoveredOperation}
                onClose={() => setSelectedOperations(null)}
                labels={labels}
              />
            )}
          <MapSelectionArea selectionBounds={selectionBounds} />
          {isLocationTagOpen && (
            <LocationPopup
              latitude={latitude}
              longitude={longitude}
              address={locationAddress || place}
              elevation={elevation}
              altitudeUnits={altitudeUnits}
              languageData={{ lat: latLabel, lng: lngLabel, amsl: amslLabel }}
            />
          )}
          <GeocoderPin
            latitude={latitude}
            longitude={longitude}
            draggable={true}
            onClick={() => {
              updateLocationTagOpen(!isLocationTagOpen);
            }}
            onDragStart={() => {
              addRemoveCircles(null);
              removeHovered();
              updateDragStatus(true);
              updateLocationTagOpen(false);
            }}
            onDragEnd={([longitude, latitude]) => {
              if (typeof longitude !== 'undefined' && typeof latitude !== 'undefined') {
                updateGeocoding({ longitude, latitude });
              }
              updateLocationAddress('');
              updateDragStatus(false);
              addRemoveCircles({ longitude, latitude });
              setTimeout(() => {
                updateLocationTagOpen(true);
              }, 1);
            }}
            onMouseEnter={() => {
              removeHovered();
              updateDragStatus(true);
            }}
            onMouseLeave={() => {
              updateDragStatus(false);
            }}
          />
          <MapControl
            mapRef={{ current: mapNode }}
            onResult={handleOnResult}
            isPinAdded={latitude && longitude ? true : false}
            addPinToCentre={addPinToCentre}
            onViewportChange={goToSelectedAddress}
            onDialogStateChange={isOpen => {
              updateDialogStatus(isOpen);
              if (isOpen) {
                removeHovered();
              }
            }}
            mapboxApiAccessToken={mapboxApiAccessToken}
            bbox={bbox}
            limit={mapBoxConfig.geocoderLimit}
            navigationControl={{ showCompass: true, showHome: true, showSearch: true }}
            translationData={{
              home: backToCenterLabel,
              search: searchLabel,
              addPin: addPinLabel,
              removePin: removePinLabel,
            }}
            resetView={resetView}
          />
        </StyledMap>
      </div>
    </div>
  );
};
